/**
 * Gallery Hero Main — stacked / fanned draggable collection slider
 * Vanilla JS, no dependencies. One instance per section (theme-editor safe).
 */
(function () {
  'use strict';

  function initGalleryHeroMain(root) {
    var stack = root.querySelector('[data-ghm-stack]');
    if (!stack) return;

    var cards = Array.prototype.slice.call(stack.querySelectorAll('[data-ghm-card]'));
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-ghm-dot]'));
    if (!cards.length) return;

    var total = cards.length;
    var activeIndex = 0;

    var stepXPercent = parseFloat(root.dataset.stepX || '36') / 100; // % of card width, per card
    var stepZ = parseFloat(root.dataset.stepZ || '200');      // px per card
    var scaleStep = parseFloat(root.dataset.scaleStep || '0.1');
    var maxVisible = parseInt(root.dataset.maxVisible || '4', 10);
    var autoplay = root.dataset.autoplay === 'true';
    var autoplaySpeed = parseInt(root.dataset.autoplaySpeed || '4000', 10);

    var dragging = false;
    var dragStartX = 0;
    var dragDeltaX = 0;
    var pointerId = null;
    var autoplayTimer = null;
    var cardWidthPx = 0;
    var resizeRaf = null;

    function measureCardWidth() {
      // Read the card's real rendered width (post-breakpoint, post-CSS) rather than
      // assuming a fixed vw offset, so the peek scales correctly at every screen size.
      cardWidthPx = cards[0].getBoundingClientRect().width || cardWidthPx;
    }

    function signedIndex(i) {
      // signed distance of card i from the active card: negative = behind/left,
      // positive = ahead/right, wrapping the shortest way around the stack.
      var diff = (i - activeIndex) % total;
      if (diff < 0) diff += total; // normalize to [0, total)
      if (diff > total / 2) diff -= total;
      return diff;
    }

    function applyTransform(card, signed, extraPx) {
      extraPx = extraPx || 0;
      var abs = Math.abs(signed);
      var visible = abs < maxVisible;
      var x = stepXPercent * cardWidthPx * signed; // px, proportional to the card's own size
      var z = -stepZ * abs;
      var scale = Math.max(1 - scaleStep * abs, 0.4);
      var opacity = visible ? 1 : 0;
      var zIndex = total - abs;

      card.style.transform =
        'translate3d(' + (x + extraPx) + 'px, 0px, ' + z + 'px) scale(' + scale + ')';
      card.style.opacity = String(opacity);
      card.style.zIndex = String(zIndex);
      card.setAttribute('data-active', signed === 0 ? 'true' : 'false');

      var link = card.querySelector('[data-ghm-link]');
      if (link) link.setAttribute('tabindex', signed === 0 ? '0' : '-1');
    }

    function render(extraPx) {
      cards.forEach(function (card, i) {
        applyTransform(card, signedIndex(i), extraPx);
      });
      dots.forEach(function (dot, i) {
        dot.setAttribute('data-active', i === activeIndex ? 'true' : 'false');
      });
    }

    function goTo(index) {
      activeIndex = ((index % total) + total) % total;
      render(0);
    }

    function next() {
      goTo(activeIndex + 1);
    }

    function prev() {
      goTo(activeIndex - 1);
    }

    // ---- Autoplay ----

    function startAutoplay() {
      if (!autoplay || total < 2) return;
      stopAutoplay();
      autoplayTimer = window.setInterval(next, autoplaySpeed);
    }

    function stopAutoplay() {
      if (autoplayTimer) {
        window.clearInterval(autoplayTimer);
        autoplayTimer = null;
      }
    }

    // ---- Drag / swipe ----

    function onPointerDown(e) {
      if (total < 2) return;
      dragging = true;
      pointerId = e.pointerId;
      dragStartX = e.clientX;
      dragDeltaX = 0;
      stack.classList.add('is-dragging');
      stopAutoplay();
      try {
        stack.setPointerCapture(pointerId);
      } catch (err) {
        /* noop */
      }
    }

    function onPointerMove(e) {
      if (!dragging) return;
      dragDeltaX = e.clientX - dragStartX;
      render(dragDeltaX * 0.6);
    }

    function onPointerUp() {
      if (!dragging) return;
      dragging = false;
      stack.classList.remove('is-dragging');

      var threshold = 70;
      if (dragDeltaX <= -threshold) {
        next();
      } else if (dragDeltaX >= threshold) {
        prev();
      } else {
        render(0);
      }
      dragDeltaX = 0;
      startAutoplay();
    }

    stack.addEventListener('pointerdown', onPointerDown);
    stack.addEventListener('pointermove', onPointerMove);
    stack.addEventListener('pointerup', onPointerUp);
    stack.addEventListener('pointercancel', onPointerUp);
    stack.addEventListener('pointerleave', function (e) {
      if (dragging && e.pointerId === pointerId) onPointerUp();
    });

    // Prevent the front card's link from firing a click right after a drag
    stack.addEventListener(
      'click',
      function (e) {
        if (Math.abs(dragDeltaX) > 5) {
          e.preventDefault();
        }
      },
      true
    );

    // Dots are clickable shortcuts
    dots.forEach(function (dot, i) {
      dot.style.cursor = 'pointer';
      dot.addEventListener('click', function () {
        goTo(i);
        stopAutoplay();
        startAutoplay();
      });
    });

    // Keyboard support when a card link is focused
    stack.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        next();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        prev();
      }
    });

    window.addEventListener('resize', function () {
      if (resizeRaf) window.cancelAnimationFrame(resizeRaf);
      resizeRaf = window.requestAnimationFrame(function () {
        measureCardWidth();
        render(0);
      });
    });

    measureCardWidth();
    render(0);
    startAutoplay();

    // Theme editor: pause/refresh cleanly
    document.addEventListener('shopify:section:unload', function (e) {
      if (e.target === root) stopAutoplay();
    });
    document.addEventListener('shopify:section:select', function (e) {
      if (root.contains(e.target)) stopAutoplay();
    });
    document.addEventListener('shopify:section:deselect', function (e) {
      if (root.contains(e.target)) startAutoplay();
    });
    document.addEventListener('shopify:block:select', function (e) {
      var card = e.target.closest ? e.target.closest('[data-ghm-card]') : null;
      if (card && root.contains(card)) {
        stopAutoplay();
        goTo(parseInt(card.dataset.index, 10));
      }
    });
  }

  function initAll() {
    document.querySelectorAll('[data-ghm-root]').forEach(initGalleryHeroMain);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-init a section instance when it's reloaded in the theme editor
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector('[data-ghm-root]');
    if (root) initGalleryHeroMain(root);
  });
})();